import {
  generateCommonRequestProperties,
  generateRequestObjectBasedOnProductCode
} from '@nw/fields-manager';
import { getQueryParamValue, SUCCESS } from '@nw/shared/utils';

import { ApplicationSectionNameEnums } from '../constants/ApplicationSectionConstants';
import ApplicationSectionProductModel from '../stores/models/ApplicationSectionProductModel';
import { ProductMachineContextType } from '../types/productMachineTypes';

import {
  EnrollmentInstallmentMetaType,
  getEnrollmentInstallmentPaymentDetails,
  parseAmountValue
} from './ProductInstallmentUtils';

type BuildPayloadParams = {
  context: ProductMachineContextType;
  mergedValues: Record<string, any>;
  userProfileDetails: any;
  sectionName: string;
  deviceDetails: Record<string, unknown>;
};

/**
 * Builds the same free-order payload shape as {@link useProductsMachine}
 * `createPaymentOrderAndGetDetail` for NIAT enrollment (amount 0 path).
 */
export const buildNiatEnrollmentFreeOrderPayload = ({
  context,
  mergedValues,
  userProfileDetails,
  sectionName,
  deviceDetails
}: BuildPayloadParams): Record<string, unknown> => {
  const productDetail = context.productDetail as ApplicationSectionProductModel;
  const {
    productCode,
    termsAndConditions,
    productFee,
    productFeeAfterDiscount,
    productRequestObjectKeys,
    enrollmentInstallmentMeta
  } = productDetail;
  const { apiStatus, inviteCode } = context;
  const values = mergedValues;

  let productRequestObjectFieldKeyValues: Record<string, unknown> = {
    languageEnum: userProfileDetails['preferred_languages']?.[0],
    communicationConsent: values['communicationConsent'],
    nxtwaveTermsAndConditions: values['nxtwaveTermsAndConditions'],
    nxtwaveRefundPolicy: values['nxtwaveRefundPolicy'],
    nxtwaveCampusChangePolicy: values['nxtwaveCampusChangePolicy']
  };

  productRequestObjectKeys?.forEach((fieldInfo) => {
    const keyToSend = fieldInfo.field_name_in_request_object;
    const fieldName = fieldInfo.field;
    productRequestObjectFieldKeyValues[keyToSend] = values[fieldName];
  });

  const isEnrollmentFeeSection =
    sectionName === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026;

  if (isEnrollmentFeeSection) {
    const course = values['course'];
    if (course != null && course !== '') {
      productRequestObjectFieldKeyValues['course'] = course;
    }
  }

  let formattedRequestObject = generateCommonRequestProperties({
    values: productRequestObjectFieldKeyValues
  });

  formattedRequestObject = generateRequestObjectBasedOnProductCode({
    productCode,
    values,
    requestObject: formattedRequestObject
  });

  const emailValue =
    values['EMAIL_ADDRESS'] ??
    values['correctEmail'] ??
    userProfileDetails['email'] ??
    '';

  const inviteCodeValue = values['inviteCode'] ?? inviteCode ?? '';

  const couponCodeValue =
    values['isCouponDiscountActionConfigApplied'] === true
      ? values['couponCode']
      : apiStatus.getCouponCodeDetailApiStatus === SUCCESS
        ? values['couponCode']
        : '';

  const amount = values['isCouponDiscountActionConfigApplied']
    ? values['amountToBePaidAfterDiscount']
    : apiStatus.getCouponCodeDetailApiStatus === SUCCESS &&
        productFeeAfterDiscount !== undefined
      ? productFeeAfterDiscount
      : productFee;

  const amountToBePaidFallback = parseAmountValue(amount) ?? 0;
  const { paymentType: resolvedEnrollmentPaymentType } =
    getEnrollmentInstallmentPaymentDetails({
      isEnrollmentFeeSection,
      enrollmentInstallmentMeta:
        (enrollmentInstallmentMeta as EnrollmentInstallmentMetaType) ?? null,
      values,
      fallbackAmount: amountToBePaidFallback
    });

  if (isEnrollmentFeeSection) {
    productRequestObjectFieldKeyValues['payment_type'] =
      resolvedEnrollmentPaymentType;
    formattedRequestObject['payment_type'] = resolvedEnrollmentPaymentType;
    formattedRequestObject['platform'] = 'application_portal';
  }

  const attemptedLicenceCode = getQueryParamValue('INVITE_CODE');
  const shouldAddAttemptedLicenceCode =
    context.referralCodes?.referral_code &&
    attemptedLicenceCode &&
    context.referralCodes.referral_code !== attemptedLicenceCode;

  if (shouldAddAttemptedLicenceCode) {
    formattedRequestObject.attempted_licence_code = attemptedLicenceCode;
  }

  const extraInfoBasedOnProductConfig: Record<string, unknown> = {
    utm_campaign: getQueryParamValue('utm_campaign') ?? '',
    utm_medium: getQueryParamValue('utm_medium') ?? '',
    utm_term: getQueryParamValue('utm_term') ?? '',
    utm_content: getQueryParamValue('utm_content') ?? '',
    utm_source: getQueryParamValue('utm_source') ?? '',
    custom_id: getQueryParamValue('custom_id') ?? '',
    program_batch: getQueryParamValue('program_batch'),
    application_name_enum: ApplicationSectionNameEnums.NIAT_APPLICATION,
    pi: getQueryParamValue('pi'),
    pm: getQueryParamValue('pm'),
    device_details: deviceDetails,
    ...formattedRequestObject
  };

  return {
    invite_code: inviteCodeValue,
    coupon_code: couponCodeValue,
    user_details: {
      full_name: userProfileDetails['name'] ?? values['FULL_NAME'] ?? '',
      email: emailValue,
      phone_number:
        userProfileDetails['phone_number'] ?? values['MOBILE_NUMBER'] ?? '',
      gender: values['GENDER'] ?? '',
      state_of_residence:
        values['stateOfResidence'] ?? values['NATIVE_STATE'] ?? ''
    },
    extra_info_based_on_product_config: JSON.stringify(
      extraInfoBasedOnProductConfig
    ),
    accepted_tnc: termsAndConditions,
    product_code: productCode,
    amount_to_be_paid_in_least_subunit: 0
  };
};
